import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useIntl } from 'react-intl';
import _ from 'lodash';
import GridContainer from '../../../components/Grid/GridContainer';
import GridItem from '../../../components/Grid/GridItem';
import Typography from '../../../components/Typography/CustomTypography';
import styles from '../../../assets/jss/pages/adminSettingCustomItemStyle';
import WrapperContainer from '../../../components/Container/WrapperContainer';
import SelectorInput from '../../../components/Input/SelectorInput';
import {
    OPTION_CUSTOM_SITE,
    OPTION_CUSTOM_PAGE_VIEWER,
    OPTION_CUSTOM_PAGE_ORGANIZER,
} from '../../../common/Constants';
import CleanableInput from '../../../components/Input/CleanableInput';
import Button from '../../../components/Button/CustomButton';
import Validation from '../../../common/Validation';
import {
    CustomItemLivestreamSiteOrganDefault,
    CustomItemSeminarSiteOrganDefault,
    CustomItemExhibitionSiteOrganDefault,
    CustomItemSidebarSiteOrganDefault,
    CustomItemCompanySiteOrganDefault,
} from './ItemSettingNameOrganizer';
import {
    CustomItemSidebarSiteViewerDefault,
    CustomItemExhibitionSiteViewerDefault,
    CustomItemSeminarSiteViewerDefault,
    CustomItemLivestreamSiteViewerDefault,
    CustomItemEventSiteViewerDefault,
    CustomItemPersonInfoSiteViewerDefault,
} from './ItemSettingNameViewer';
import ObjectHelper from '../../../common/helper/ObjectHelper';
import Snackbar from '../../../components/Snackbar/Snackbar';
import AlertDialog from '../../../components/Dialog/AlertDialog';

const useStyles = makeStyles(styles);

const SettingCustomName = ({
    getSettingCustomText,
    clearGetSettingCustomText,
    updateSettingCustomText,
    clearUpdateSettingCustomText,
    customText,
    updateCustomText,
    eventInfo,
}) => {
    const classes = useStyles();
    const { shortName } = useParams();
    const intl = useIntl();
    const [params, setParams] = useState({
        site_custom: 0,
        page_custom: 0,
    });
    const [paramsLang, setParamsLang] = useState({});
    const [validateResults, setValidateResults] = useState({});
    const [customItem, setCustomItem] = useState(CustomItemLivestreamSiteViewerDefault);
    const [isOpenSnackbar, setIsOpenSnackbar] = useState({ isOpen: false, message: '' });
    const [isOpenConfirmSuccess, setIsOpenConfirmSuccess] = useState({ open: false, content: '', type: '' });

    useEffect(() => {
        getSettingCustomText({
            site: 0,
            text_type: 0,
            eventShortName: shortName,
        });
        return () => {
            clearGetSettingCustomText();
            clearUpdateSettingCustomText();
        };
    }, []);

    useEffect(() => {
        if (!_.isEmpty(customText)) {
            setParams({
                site_custom: customText.site,
                page_custom: customText.text_type,
                ...customText.content,
            });
            if (!_.isEmpty(eventInfo.languages)) {
                setParamsLang({
                    ...customText?.content_en,
                });
            }
        }
    }, [customText]);

    useEffect(() => {
        if (+params.site_custom === 1) {
            if (+params.page_custom === 1) {
                setCustomItem(CustomItemSeminarSiteOrganDefault(params));
            } else if (+params.page_custom === 2) {
                setCustomItem(CustomItemExhibitionSiteOrganDefault(params));
            } else if (+params.page_custom === 3) {
                setCustomItem(CustomItemSidebarSiteOrganDefault(params));
            } else if (+params.page_custom === 4) {
                setCustomItem(CustomItemCompanySiteOrganDefault(params, paramsLang, !_.isEmpty(eventInfo.languages)));
            } else if (+params.page_custom === 0) {
                setCustomItem(CustomItemLivestreamSiteOrganDefault(params));
            }
        } else if (+params.site_custom === 0) {
            if (+params.page_custom === 1) {
                setCustomItem(CustomItemSeminarSiteViewerDefault(params, paramsLang, !_.isEmpty(eventInfo.languages)));
            } else if (+params.page_custom === 2) {
                setCustomItem(CustomItemExhibitionSiteViewerDefault(params, paramsLang, !_.isEmpty(eventInfo.languages)));
            } else if (+params.page_custom === 3) {
                setCustomItem(CustomItemSidebarSiteViewerDefault(params, paramsLang, !_.isEmpty(eventInfo.languages)));
            } else if (+params.page_custom === 5) {
                setCustomItem(CustomItemEventSiteViewerDefault(params, paramsLang, !_.isEmpty(eventInfo.languages)));
            } else if (+params.page_custom === 6) {
                setCustomItem(CustomItemPersonInfoSiteViewerDefault(params, paramsLang, !_.isEmpty(eventInfo.languages)));
            } else if (+params.page_custom === 0) {
                setCustomItem(CustomItemLivestreamSiteViewerDefault(params, paramsLang, !_.isEmpty(eventInfo.languages)));
            }
        }
    }, [params, paramsLang]);

    useEffect(() => {
        if (!_.isEmpty(updateCustomText)) {
            setIsOpenSnackbar({ isOpen: true, message: '更新しました。' });
            setParams({
                site_custom: customText.site,
                page_custom: customText.text_type,
            });
            getSettingCustomText({
                site: updateCustomText.site,
                text_type: updateCustomText.text_type,
                eventShortName: shortName,
            });
            clearUpdateSettingCustomText();
        }
    }, [updateCustomText]);

    const handleSetParams = (field, fieldItem, value) => {
        if (!_.isEmpty(fieldItem)) {
            setParams((prevParams) => ({ ...prevParams, [fieldItem]: { ...prevParams[fieldItem], [field]: value.trim() } }));
        } else setParams((prevParams) => ({ ...prevParams, [field]: value.trim() }));
    };

    const handleSetParamsLang = (field, fieldItem, value) => {
        if (!_.isEmpty(fieldItem)) {
            setParamsLang((prevParams) => ({ ...prevParams, [fieldItem]: { ...prevParams[fieldItem], [field]: value.trim() } }));
        } else setParamsLang((prevParams) => ({ ...prevParams, [field]: value.trim() }));
    };

    const validate = () => {
        const validateRules = {};
        Object.entries(customItem).forEach((data) => {
            // eslint-disable-next-line no-unused-expressions
            !_.isEmpty(data[1]) && data[1].forEach((item) => {
                validateRules[item.lang ? `${item.key}_en` : `${item.key}`] = item.lang
                    ? [
                        () => !_.isEmpty(eventInfo.languages) && item.value && Validation.validateLatin(item.value),
                        () => !_.isEmpty(eventInfo.languages) && item.value && Validation.validateMaxLength(item.value, 50),
                    ]
                    : [
                        () => item.value && Validation.validateMaxLength(item.value, 20),
                    ];
            });
        });
        const validateResult = Validation.validateAllRules(validateRules);

        if (Object.keys(validateResult).length > 0) {
            setValidateResults({ ...validateResult });
            window.scrollTo(0, 0);
            return false;
        }
        setValidateResults({});
        return true;
    };

    const handleSubmit = () => {
        if (validate()) {
            setIsOpenConfirmSuccess({ open: true, content: '適用して宜しいですか。', type: 'submit' });
        }
    };
    const handleCancel = () => {
        setValidateResults({});
        getSettingCustomText({
            site: +params.site_custom,
            text_type: +params.page_custom,
            eventShortName: shortName,
        });
    };

    const handleReset = () => {
        setValidateResults({});
        let sbData = {
            site: +params.site_custom,
            text_type: +params.page_custom,
            content: {},
        };
        if ((!+params.site_custom || (+params.page_custom === 4 && +params.site_custom)) && !_.isEmpty(eventInfo.languages)) {
            sbData = {
                ...sbData,
                content_en: {},
            };
        }
        updateSettingCustomText({
            eventShortName: shortName,
            data: sbData,
        });
    };

    const handleChangeSite = (site) => {
        setValidateResults({});
        getSettingCustomText({
            site: +site,
            text_type: 0,
            eventShortName: shortName,
        });
    };

    const handleChangePageOfSite = (page) => {
        setValidateResults({});
        getSettingCustomText({
            site: +params.site_custom,
            text_type: +page,
            eventShortName: shortName,
        });
    };

    const handleSuccessDialog = (type) => {
        setIsOpenConfirmSuccess({ open: false, content: '', type: '' });
        if (type === 'submit') {
            const dataSb = ObjectHelper.removePropertyByKey('site_custom')(params);
            const submitDB = (!+params.site_custom || (+params.page_custom === 4 && +params.site_custom)) && !_.isEmpty(eventInfo.languages) ? {
                eventShortName: shortName,
                data: {
                    site: +params.site_custom,
                    text_type: +params.page_custom,
                    content: ObjectHelper.removePropertyByKey('page_custom')(dataSb),
                    content_en: paramsLang,
                },
            } : {
                eventShortName: shortName,
                data: {
                    site: +params.site_custom,
                    text_type: +params.page_custom,
                    content: ObjectHelper.removePropertyByKey('page_custom')(dataSb),
                },
            };
            updateSettingCustomText(submitDB);
        } else if (type === 'reset') {
            handleReset();
        } else if (type === 'cancel') {
            handleCancel();
        }
    };

    return (
        <>
            <GridContainer className={classes.custom}>
                <WrapperContainer>
                    <GridContainer className={classes.customSite}>
                        <GridItem md={3} xs={4}>
                            <Typography bold>反映するサイト</Typography>
                        </GridItem>
                        <GridItem md={3} xs={4}>
                            <Typography bold>変更画面対象</Typography>
                        </GridItem>
                    </GridContainer>
                    <GridContainer className={classes.customSite}>
                        <GridItem md={3} xs={4}>
                            <SelectorInput
                                valueSelected={+params.site_custom}
                                optionList={OPTION_CUSTOM_SITE}
                                onChange={(valueInput) => handleChangeSite(valueInput)}
                            />
                        </GridItem>
                        <GridItem md={3} xs={4}>
                            <SelectorInput
                                valueSelected={+params.page_custom}
                                optionList={+params.site_custom ? OPTION_CUSTOM_PAGE_ORGANIZER : OPTION_CUSTOM_PAGE_VIEWER}
                                onChange={(valueInput) => handleChangePageOfSite(valueInput)}
                            />
                        </GridItem>
                    </GridContainer>
                    {Object.entries(customItem).map((item, index) => (
                        <>
                            {!_.isEmpty(item[1]) && (
                                // eslint-disable-next-line react/no-array-index-key
                                <GridItem className={classes.customSite} key={`${item.toString()}_${index}`}>
                                    <fieldset className={classes.fieldset}>
                                        <legend>
                                            <Typography variant="h5" color="primary" bold>
                                                {
                                                    // eslint-disable-next-line no-nested-ternary
                                                    item[0] === 'screen_name'
                                                        ? 'Screen name'
                                                        : item[0] === 'field_name'
                                                            ? 'Field name'
                                                            : 'Button name'
                                                }
                                            </Typography>
                                        </legend>
                                        {item[1].map((screen, indexScreen) => screen.name && (
                                            // show field english
                                            (screen.isShow || (+params.site_custom && +params.page_custom !== 4)) ? (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <GridContainer className={classes.customSite} key={`${screen.toString()}_${indexScreen}`}>
                                                    <GridItem md={2}>
                                                        {screen.name}
                                                    </GridItem>
                                                    <GridItem md={7}>
                                                        <CleanableInput
                                                            defaultValue={screen.value}
                                                            onChange={(value) => (!screen?.lang ? handleSetParams(screen.key, item[0], value) : handleSetParamsLang(screen.key, item[0], value))}
                                                            error={!!validateResults[screen.lang ? `${screen.key}_en` : screen.key]}
                                                            helperText={validateResults[screen.lang ? `${screen.key}_en` : screen.key]
                                                                ? intl.formatMessage({ id: validateResults[screen.lang ? `${screen.key}_en` : screen.key].message }, { maxLength: screen.lang ? 50 : 20 }) : ''}
                                                        />
                                                    </GridItem>
                                                </GridContainer>
                                            ) : ''
                                        ))}
                                    </fieldset>
                                </GridItem>
                            )}
                        </>
                    ))}
                    <GridContainer className={classes.customFooter}>
                        <GridItem className={classes.customFooterButton}>
                            <Button
                                color="info"
                                round
                                gradient
                                size="md"
                                onClick={handleSubmit}
                                className={classes.customButton}
                            >
                                <b>Apply</b>
                            </Button>
                            <Button
                                color="info"
                                round
                                gradient
                                size="md"
                                onClick={() => setIsOpenConfirmSuccess({ open: true, content: 'リセットして宜しいですか。', type: 'reset' })}
                                className={classes.customButtonReset}
                            >
                                <b>Reset</b>
                            </Button>
                            <Button
                                color="info"
                                round
                                gradient
                                size="md"
                                onClick={() => setIsOpenConfirmSuccess({ open: true, content: '入力した情報をキャンセルして宜しいですか。', type: 'cancel' })}
                                className={classes.customButton}
                            >
                                <b>Cancel</b>
                            </Button>
                        </GridItem>
                    </GridContainer>
                </WrapperContainer>
            </GridContainer>
            <Snackbar
                open={isOpenSnackbar.isOpen}
                onClose={() => setIsOpenSnackbar({ isOpen: false, message: '' })}
                message={isOpenSnackbar.message}
                severity="success"
            />
            <AlertDialog
                title="確認ダイアログ"
                handleClose={() => setIsOpenConfirmSuccess({ open: false, content: '', type: '' })}
                handleOpen={isOpenConfirmSuccess.open}
                handleSubmit={() => handleSuccessDialog(isOpenConfirmSuccess.type)}
                contents={isOpenConfirmSuccess.content}
            />
        </>
    );
};

SettingCustomName.defaultProps = {
    getSettingCustomText: () => {},
    clearGetSettingCustomText: () => {},
    updateSettingCustomText: () => {},
    clearUpdateSettingCustomText: () => {},
    customText: {},
    updateCustomText: {},
    eventInfo: {},
};

SettingCustomName.propTypes = {
    getSettingCustomText: PropTypes.func,
    clearGetSettingCustomText: PropTypes.func,
    updateSettingCustomText: PropTypes.func,
    clearUpdateSettingCustomText: PropTypes.func,
    customText: PropTypes.oneOfType([() => null, PropTypes.object]),
    updateCustomText: PropTypes.oneOfType([() => null, PropTypes.object]),
    eventInfo: PropTypes.oneOfType([() => null, PropTypes.object]),
};

export default SettingCustomName;
